import { useSiteData } from '../context/DataContext.jsx'
import { Link } from 'react-router-dom'
import RailBlock from '../components/RailBlock.jsx'
import Chip from '../components/Chip.jsx'
import ErrorFallback from '../components/ErrorFallback.jsx'
import Skeleton from '../components/Skeleton.jsx'

function formatYear(dateString) {
  if (!dateString) return ''
  return new Date(dateString).getFullYear()
}

function BlogListPage() {
  const { data: siteData, loading, error, refetch } = useSiteData()
  const data = siteData?.blogPosts || []

  if (loading) {
    return (
      <div className="pg-layout">
        <div>
          <Skeleton className="h-4 w-32 mb-3" />
          <Skeleton className="h-14 w-48 mb-3" />
          <Skeleton className="h-5 w-80 mb-10" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="border-t border-soft-rule py-7">
              <div className="grid grid-cols-1 sm:grid-cols-[80px_1fr_200px] gap-4 sm:gap-8">
                <Skeleton className="h-3 w-10" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-56" />
                  <Skeleton className="h-3 w-full max-w-[540px]" />
                </div>
                <Skeleton className="h-3 w-32 justify-self-end" />
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (error) return <ErrorFallback message="Failed to load blog posts" onRetry={refetch} />

  const posts = data || []

  // Gather unique tags and years
  const allTags = [...new Set(posts.flatMap((p) => p.tag_list || []))]
  const allYears = [...new Set(posts.map((p) => formatYear(p.published_at)).filter(Boolean))].sort((a, b) => b - a)

  return (
    <div className="pg-layout">
      {/* LEFT */}
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-3">
          Writing · {posts.length} posts
        </p>
        <h1 className="text-[36px] sm:text-[48px] md:text-[56px] font-normal leading-[1.02] tracking-[-1.8px] mb-3">
          Writing
        </h1>
        <p className="text-[18px] leading-[1.5] text-soft-dim mb-10">
          Notes on building things, breaking things, figuring out why, or just things i find intriguing.
        </p>

        <div>
          {posts.map((post, index) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className={`grid grid-cols-[80px_1fr_200px] gap-8 py-7 no-underline group ${
                index > 0 ? 'border-t border-soft-rule' : 'border-t border-soft-rule'
              }`}
            >
              <span className="font-mono text-[12px] text-soft-dim self-baseline">
                {formatYear(post.published_at)}
              </span>
              <div>
                <h2 className="text-[20px] font-medium leading-[1.2] tracking-[-0.3px] text-soft-ink mb-2 group-hover:text-soft-dim transition-colors">
                  {post.title}
                </h2>
                {post.excerpt && (
                  <p className="text-[15px] leading-[1.55] text-soft-dim max-w-[540px]">
                    {post.excerpt}
                  </p>
                )}
              </div>
              <div className="text-right self-baseline">
                <p className="font-mono text-[12px] text-soft-faint">
                  {post.tag_list?.join(' · ') || `${post.read_time} min`}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* RIGHT rail */}
      <aside>
        <RailBlock eyebrow="Tags" top>
          <div className="flex flex-wrap gap-2">
            {allTags.map((tag) => (
              <Chip key={tag} active={false}>
                {tag}
              </Chip>
            ))}
          </div>
        </RailBlock>

        <RailBlock eyebrow="Archive">
          <div className="divide-y divide-soft-rule">
            {allYears.map((year) => (
              <div key={year} className="flex justify-between items-center py-2.5">
                <span className="text-[14px] text-soft-ink">{year}</span>
                <span className="font-mono text-[11px] text-soft-faint">
                  {posts.filter((p) => formatYear(p.published_at) === year).length} posts
                </span>
              </div>
            ))}
          </div>
        </RailBlock>
      </aside>
    </div>
  )
}

export default BlogListPage
